import React from 'react';
import PropTypes from 'prop-types';
import Form, {Input, Select} from 'react-form-controlled';
import Validate from 'react-form-controlled-validate';
import { AsYouTypeFormatter } from 'google-libphonenumber/dist/libphonenumber';
import MessageDefinitions from '../i18n/MessageDefinitions';
import autobind from 'autobind-decorator';
import { FormattedMessage, intlShape } from 'react-intl';
import {selectInput, textInput} from '../../common/util/FormInput';
import FontAwesome from 'react-fontawesome';
import {saveLead, changeAppointmentFlow} from "../actions/ContactMeActionCreators";
import Screens from '../constants/Screens';
import {connectToStores} from "fluxible-addons-react";

function pruneState(inState = {}) {
  const outState = {};
  Object.keys(inState || {}).forEach(key =>{
    if (inState[key] !== '') {
      outState[key] = inState[key];
    }
  });

  return outState;
}

@connectToStores(['ContactMeStore'], (context, props) => {
  const ContactMeStore = context.getStore('ContactMeStore');
  return {
    currentLead: ContactMeStore.getLead()
  };
})
class ContactForm extends React.Component {
  static propTypes = {
    currentLead: PropTypes.object,
    fields: PropTypes.arrayOf(PropTypes.string),
    schema: PropTypes.object,
    useAppointmentFlow: PropTypes.bool
  };

  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    intl: intlShape
  };

  static defaultBusinessSchema = {
    type: 'object',
    properties: {
      firstName: {
        type: 'string',
        minLength: 1
      },
      lastName: {
        type: 'string',
        minLength: 1
      },
      businessName: {
        type: 'string',
        minLength: 1
      },
      email: {
        type: 'string',
        format: 'email'
      },
      phone: {
        type: 'string',
        'pattern': '^\\([0-9]{3}\\)\\s*[0-9]{3}-[0-9]{4}$|^[0-9]{10}$'
      },
      customerId: {
        type: 'string',
        minLength: 1
      },
      businessId: {
        type: 'string',
        minLength: 1
      },
      businessDba: {
        type: 'string',
        minLength: 1
      },
      preferredTime: {
        type: 'string',
        enum: ['morning','afternoon','evening','noPreference']
      }
    },
    required: ['firstName', 'lastName', 'email', 'phone', 'businessName']
  };

  static defaultConsumerSchema = {
    type: 'object',
    properties: {
      firstName: {
        type: 'string',
        minLength: 1
      },
      lastName: {
        type: 'string',
        minLength: 1
      },
      email: {
        type: 'string',
        format: 'email'
      },
      phone: {
        type: 'string',
        'pattern': '^\\([0-9]{3}\\)\\s*[0-9]{3}-[0-9]{4}$|^[0-9]{10}$'
      },
      customerId: {
        type: 'string',
        minLength: 1
      },
      preferredTime: {
        type: 'string',
        enum: ['morning','afternoon','evening','noPreference']
      }
    },
    required: ['firstName', 'lastName', 'email', 'phone']
  };

  static defaultConsumerFields = ['firstName', 'lastName', 'email', 'phone'];
  static defaultBusinessFields = ['firstName', 'lastName', 'businessName', 'email', 'phone'];

  constructor(props) {
    super(props);
    const { currentLead = {} } = props;
    this.state = {
      errors: [],
      form: currentLead,
      processing: false
    };
  }

  @autobind
  validateForm(state) {
    const prunedState = pruneState(state);
    this.form.validate(prunedState).then((formErrors = []) => {
      const errors = formErrors.map(error => {
        return {
          key: error.path,
          errors: [error.message]
        };
      });

      this.setState({
        errors
      });
    });
  }

  componentDidMount() {
    this.validateForm(this.state.form || {});
  }

  @autobind
  valueChanged(state) {
    const lead = {
      ...state
    };
    if (lead.phone) {
      lead.phone = state.phone.replace(/\D/g, '');
    }
    this.validateForm(lead);

    this.setState({
      form: lead
    });
  }

  @autobind
  formSubmitted(state) {
    const lead = {
      ...state
    };
    this.setState({
      processing: true
    });
    lead.phone = state.phone.replace(/\D/g, '');

    if (window.ignite.preContactSubmitted) {
      window.ignite.preContactSubmitted(lead);
    }

    window.ignite.contactService.submitContact(lead).then(result => {
      this.setState({
        processing: false
      });
      if (result.status === 'success') {
        console.log('contact submitted successfully');
        if(this.props.useAppointmentFlow){
          window.ignite.contactService.sendLead();
          const nextScreen = Screens.LOCATION;
          this.context.executeAction(saveLead, {lead});
          this.context.executeAction(changeAppointmentFlow, {nextScreen});
        }
        else if (window.ignite.contactSubmitted) {
          window.ignite.contactSubmitted(result);
        }
      }
    });


  }

  render() {
    const formatMessage = this.context.intl.formatMessage;
    const { fields = ContactForm.defaultConsumerFields, schema = ContactForm.defaultConsumerSchema, useAppointmentFlow } = this.props;
    const { errors = [], processing } = this.state;

    const formValid = !errors.length;

    const value = pruneState(this.state.form);

    if (value.phone) {
      const formatter = new AsYouTypeFormatter('US');
      let phoneNumber = null;

      for (const n of value.phone || []) {
        phoneNumber = formatter.inputDigit(n);
      }

      value.phone = phoneNumber;
    }

    const fieldErrors = {};

    errors.forEach(error => {
      fieldErrors[error.key] = true;
    });

    const formFields = fields.map(field => {
      let type = 'text';
      switch (field) {
        case 'email':
          type = 'email';
          break;

        case 'phone':
          type = 'tel';
          break;

        case 'preferredTime':
          return selectInput({
            name: field,
            options: {
              morning: 'Morning',
              afternoon: 'Afternoon',
              evening: 'Evening',
              noPreference: 'No preference'
            },
            labelMessage: MessageDefinitions[field],
            placeholderMessage: MessageDefinitions[`${field}Placeholder`],
            errorMessage: MessageDefinitions[`${field}Error`],
            hasError: fieldErrors[field],
            formatMessage
          });

        default:
          break;
      }
      return textInput({
        type,
        name: field,
        labelMessage: MessageDefinitions[field],
        placeholderMessage: MessageDefinitions[`${field}Placeholder`],
        errorMessage: MessageDefinitions[`${field}Error`],
        hasError: fieldErrors[field],
        formatMessage
      });
    });

    const buttonMessage =
      processing
        ? <FontAwesome name="refresh" spin/>
        : useAppointmentFlow
          ? <FormattedMessage {...MessageDefinitions.continue} />
          : <FormattedMessage {...MessageDefinitions.submit} />;

    return (
      <Validate schema={schema} ref={f => this.form = f}>
        <Form value={value}
              className="form-container contactMeStage"
              onChange={this.valueChanged}
              onSubmit={this.formSubmitted}>
          <div className="form-body">
            {formFields}
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary save" disabled={!formValid || processing}>
              {buttonMessage}
            </button>
          </div>
        </Form>
      </Validate>
    );
  }
}

export default ContactForm;



/** WEBPACK FOOTER **
 ** ./src/main/js/contactMe/components/ContactForm.js
 **/